import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Container,
  Typography,
  TextField,
  Button,
  Paper,
  Box,
  Stepper,
  Step,
  StepLabel,
  FormControlLabel,
  Radio,
  RadioGroup,
  InputAdornment,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
  LinearProgress,
  Collapse,
  Snackbar,
  useMediaQuery,
  ThemeProvider,
  createTheme,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { styled } from '@mui/system';
import {
  Smartphone,
  Phone,
  ArrowRight,
  ArrowLeft,
  Check,
  AlertCircle,
  ChevronDown,
  Info as InfoIcon,
  Package,
  Calendar,
  MapPin,
  Hash,
  CheckCircle,
  MessageSquare,
  BellRing,
  Zap,
  Monitor,
  Tablet,
} from 'lucide-react';
import { motion } from 'framer-motion';
import ReviewAndConfirm from './ReviewAndConfirm/ReviewAndConfirm';
import SubscriptionAndShipping from './SubscriptionAndShipping';
import Payment from './Payment';
import { trackStarted } from '../../utils/analytics';
import './SwitchingCarriers.css';

// Enhanced Theme Configuration
const theme = createTheme({
  palette: {
    primary: {
      main: '#ff385c',
      light: '#ffeaee',
    },
    secondary: {
      main: '#00a699',
      light: '#e6f7f6',
    },
    info: {
      main: '#6366F1',
      light: '#eef0ff',
    },
    purple: {
      main: '#9333EA',
      light: '#f5ebff',
    },
    error: {
      main: '#ef4444',
      light: '#fee2e2',
    },
    warning: {
      main: '#f59e0b',
      light: '#fef3c7',
    },
    success: {
      main: '#10b981',
      light: '#d1fae5',
    },
    background: {
      default: '#f9fafb',
    },
    text: {
      primary: '#111827',
      secondary: '#6b7280',
    },
  },
  typography: {
    fontFamily: '"Inter", "Segoe UI", "Roboto", "Helvetica Neue", sans-serif',
    h5: {
      fontWeight: 700,
      fontSize: '1.5rem',
      lineHeight: 1.3,
    },
    h6: {
      fontWeight: 600,
      fontSize: '1.125rem',
      lineHeight: 1.4,
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.5,
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.5,
    },
    subtitle1: {
      fontSize: '1rem',
      fontWeight: 500,
      lineHeight: 1.4,
    },
  },
  shape: {
    borderRadius: 12,
  },
  shadows: [
    'none',
    '0px 1px 2px rgba(0, 0, 0, 0.05)',
    '0px 1px 3px rgba(0, 0, 0, 0.1), 0px 1px 2px rgba(0, 0, 0, 0.06)',
    '0px 4px 6px -1px rgba(0, 0, 0, 0.1), 0px 2px 4px -1px rgba(0, 0, 0, 0.06)',
    '0px 10px 15px -3px rgba(0, 0, 0, 0.1), 0px 4px 6px -2px rgba(0, 0, 0, 0.05)',
    '0px 20px 25px -5px rgba(0, 0, 0, 0.1), 0px 10px 10px -5px rgba(0, 0, 0, 0.04)',
    '0px 25px 50px -12px rgba(0, 0, 0, 0.25)',
  ],
});

// Enhanced Styled Components
const StyledContainer = styled(Container)(({ theme }) => ({
  paddingTop: theme.spacing(6),
  paddingBottom: theme.spacing(10),
  maxWidth: '800px',
}));

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.shadows[3],
  backgroundColor: '#ffffff',
  position: 'relative',
  overflow: 'hidden',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(3),
  },
}));

const ProgressIndicator = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  zIndex: 10,
}));

const SectionContainer = styled(motion.div)(({ theme, borderColor = theme.palette.primary.main, bgColor = theme.palette.primary.light }) => ({
  marginBottom: theme.spacing(3),
  padding: theme.spacing(3),
  backgroundColor: '#FFFFFF',
  borderRadius: theme.shape.borderRadius,
  border: '1px solid #eaeaea',
  borderLeft: `4px solid ${borderColor}`,
  boxShadow: theme.shadows[1],
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: bgColor,
    opacity: 0.08,
    zIndex: 0,
    pointerEvents: 'none',
  },
  '& > *': {
    position: 'relative',
    zIndex: 1,
  },
}));

const LineContainer = styled(SectionContainer)(({ theme }) => ({
  borderLeftColor: theme.palette.primary.main,
  '&::before': {
    backgroundColor: theme.palette.primary.light,
  },
}));

const LocationContainer = styled(SectionContainer)(({ theme }) => ({
  borderLeftColor: theme.palette.info.main,
  '&::before': {
    backgroundColor: theme.palette.info.light,
  },
}));

const PreviousCarrierContainer = styled(SectionContainer)(({ theme }) => ({
  borderLeftColor: theme.palette.purple.main,
  '&::before': {
    backgroundColor: theme.palette.purple.light,
  },
}));

const SectionTitle = styled(Typography)(({ theme, color = theme.palette.primary.main }) => ({
  fontWeight: 600,
  fontSize: '1.1rem',
  marginBottom: theme.spacing(2),
  color: color,
  textAlign: 'center',
  position: 'relative',
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: -8,
    left: '50%',
    transform: 'translateX(-50%)',
    width: '40px',
    height: '2px',
    backgroundColor: color,
    borderRadius: '1px',
  },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  '& .MuiOutlinedInput-root': {
    borderRadius: '10px',
    transition: 'all 0.2s ease-in-out',
    backgroundColor: '#F9FAFB',
    '&:hover': {
      backgroundColor: '#F3F4F6',
    },
    '&.Mui-focused': {
      backgroundColor: '#FFFFFF',
      boxShadow: '0 0 0 3px rgba(255, 56, 92, 0.12)',
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.primary.main,
    },
  },
  '& .MuiInputLabel-root': {
    fontWeight: 500,
  },
  '& .MuiInputLabel-outlined.MuiInputLabel-shrink': {
    fontWeight: 600,
  },
  '& .MuiInputAdornment-root': {
    '& svg': {
      color: theme.palette.text.secondary,
    },
  },
}));

const ColoredStepIcon = styled('div')(({ theme, ownerState }) => ({
  backgroundColor: ownerState.active ? theme.palette.primary.main : ownerState.completed ? theme.palette.success.main : '#E5E7EB',
  color: '#fff',
  width: 36,
  height: 36,
  display: 'flex',
  borderRadius: '50%',
  justifyContent: 'center',
  alignItems: 'center',
  transition: 'all 0.3s ease-in-out',
  boxShadow: ownerState.active || ownerState.completed ? '0 4px 6px rgba(0, 0, 0, 0.1)' : 'none',
}));

const StepConnector = styled(Box)(({ theme, active, completed }) => ({
  flex: 1,
  height: 2,
  backgroundColor: completed ? theme.palette.success.main : active ? theme.palette.primary.main : '#E5E7EB',
  transition: 'background-color 0.3s ease-in-out',
}));

const ActionButton = styled(Button)(({ theme, isback = false }) => ({
  borderRadius: '10px',
  padding: '12px 24px',
  fontWeight: 600,
  textTransform: 'none',
  boxShadow: isback ? 'none' : theme.shadows[2],
  transition: 'all 0.2s ease-in-out',
  ...(isback
    ? {
      borderColor: '#6b7280',
      color: '#6b7280',
      '&:hover': {
        backgroundColor: '#F3F4F6',
        borderColor: '#4b5563',
      }
    }
    : {
      backgroundColor: '#0066ff',
      color: '#FFFFFF',
      '&:hover': {
        backgroundColor: '#0052cc',
        boxShadow: theme.shadows[3],
        transform: 'translateY(-1px)',
      },
      '&:active': {
        transform: 'translateY(0)',
        boxShadow: theme.shadows[1],
      }
    }
  ),
}));

const InstructionAccordion = styled(Accordion)(({ theme }) => ({
  marginTop: theme.spacing(3),
  border: '1px solid #eaeaea',
  borderRadius: '10px !important',
  boxShadow: 'none',
  overflow: 'hidden',
  '&::before': {
    display: 'none',
  },
  '& .MuiAccordionSummary-root': {
    minHeight: '56px',
    padding: '0 16px',
    '&:hover': {
      backgroundColor: '#F9FAFB',
    },
  },
  '& .MuiAccordionSummary-content': {
    gap: '8px',
  },
  '& .MuiAccordionDetails-root': {
    padding: '16px',
    backgroundColor: '#F9FAFB',
  },
}));

const RadioOptionBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  background: '#F9FAFB',
  borderRadius: '10px',
  padding: '12px 16px',
  border: '1px solid #eaeaea',
  marginBottom: theme.spacing(3),
  marginTop: theme.spacing(1),
  '& .MuiFormControlLabel-root': {
    marginLeft: 0,
    marginRight: 0,
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
  },
}));

const EsimInfoBox = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(3),
  padding: theme.spacing(2.5),
  backgroundColor: theme.palette.primary.light,
  borderRadius: '10px',
  border: `1px solid ${theme.palette.primary.light}`,
}));

const StepContainer = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(2),
}));

const StepItem = styled(ListItem)(({ theme }) => ({
  padding: theme.spacing(0.5, 0),
  '& .MuiListItemIcon-root': {
    minWidth: '32px',
  },
}));

const ImeiMethodContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: theme.spacing(1.5),
  marginBottom: theme.spacing(2),
  '& svg': {
    color: theme.palette.primary.main,
    flexShrink: 0,
    marginTop: '3px',
  },
}));

const DeviceOption = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: theme.spacing(1),
  padding: theme.spacing(2),
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    backgroundColor: theme.palette.background.default,
    transform: 'translateY(-2px)',
    boxShadow: theme.shadows[1],
  },
}));

// Steps array
const steps = [
  'Device Compatibility',
  'Location',
  'Previous Carrier',
  'Subscription & Shipping',
  'Review & Confirm',
  'Payment'
];

function ColoredStepIconComponent(props) {
  const { active, completed, className, icon } = props;
  return (
    <ColoredStepIcon ownerState={{ active, completed }} className={className}>
      {completed ? <Check size={20} /> : icon}
    </ColoredStepIcon>
  );
}

const CustomStepper = ({ activeStep, steps }) => {
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  if (isMobile) {
    return (
      <Stepper activeStep={activeStep} orientation="vertical">
        {steps.map((label, index) => (
          <Step key={label}>
            <StepLabel StepIconComponent={ColoredStepIconComponent}>
              {label}
            </StepLabel>
          </Step>
        ))}
      </Stepper>
    );
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', px: 2, pb: 3 }}>
      {steps.map((label, index) => (
        <React.Fragment key={label}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <ColoredStepIconComponent
              active={activeStep === index}
              completed={activeStep > index}
              icon={index + 1}
            />
            <Typography
              variant="body2"
              sx={{
                mt: 1,
                color: activeStep === index ? 'primary.main' : activeStep > index ? 'success.main' : 'text.secondary',
                fontWeight: activeStep === index ? 600 : 400,
                fontSize: '0.75rem'
              }}
            >
              {label}
            </Typography>
          </Box>
          {index < steps.length - 1 && (
            <StepConnector
              active={activeStep > index}
              completed={activeStep > index + 1}
            />
          )}
        </React.Fragment>
      ))}
    </Box>
  );
};

const SwitchingCarriers = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const planData = location.state;
  const selectedPlan = planData?.selected_plan || planData?.planData?.selected_plan;
  const lineCount = planData?.lines || 1;
  const carrierName = planData?.planData?.carrier?.name || 'Carrier';

  // State Management
  const [activeStep, setActiveStep] = useState(0);

  // Multiple IMEI and eSIM states
  const [imeis, setImeis] = useState(Array(lineCount).fill(''));
  const [esimOptions, setEsimOptions] = useState(Array(lineCount).fill('no'));
  const [imei2Values, setImei2Values] = useState(Array(lineCount).fill(''));
  const [imeiValidation, setImeiValidation] = useState(Array(lineCount).fill(true));

  // Multiple phone number states
  const [phoneNumbers, setPhoneNumbers] = useState(Array(lineCount).fill(''));
  const [phoneNumberValidation, setPhoneNumberValidation] = useState(Array(lineCount).fill(true));

  const [portNumber, setPortNumber] = useState('no');
  const [previousCarrier, setPreviousCarrier] = useState('');
  const [previousBill, setPreviousBill] = useState('');
  const [progress, setProgress] = useState(0);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [reviewData, setReviewData] = useState(null);
  const [subscriptionType, setSubscriptionType] = useState('3months');
  const [shippingMethod, setShippingMethod] = useState('standard');

  // Location form states
  const [zipCode, setZipCode] = useState('');
  const [areaCode, setAreaCode] = useState('');

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 30,
        staggerChildren: 0.1
      }
    },
    exit: { opacity: 0, y: -20 }
  };

  useEffect(() => {
    setProgress((activeStep / (steps.length - 1)) * 100);
  }, [activeStep]);

  useEffect(() => {
    trackStarted(carrierName);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    console.log('eSIM options changed:', esimOptions);
  }, [esimOptions]);

  useEffect(() => {
    console.log('Location State:', location.state);
    console.log('Plan Data:', planData);
    console.log('Number of lines:', lineCount);
  }, [location.state, planData, lineCount]);

  const validateIMEI = (imei) => {
    return /^\d{15}$/.test(imei);
  };

  const validateAllIMEIs = () => {
    // At least one IMEI should be valid or all can be empty (making them optional)
    return imeis.some(imei => validateIMEI(imei)) || imeis.every(imei => imei === '');
  };

  const validatePhoneNumber = (phoneNumber) => {
    return /^\d{10}$/.test(phoneNumber);
  };

  const validateAllPhoneNumbers = () => {
    // When porting is enabled, each phone number must be valid
    return phoneNumbers.every(phone => validatePhoneNumber(phone));
  };

  const validateZipCode = (zipCode) => {
    return /^\d{5}(-\d{4})?$/.test(zipCode);
  };

  const validateForm = () => {
    switch (activeStep) {
      case 0:
        return validateAllIMEIs();
      case 1:
        if (portNumber === 'yes') {
          return validateAllPhoneNumbers();
        } else {
          return validateZipCode(zipCode);
        }
      case 2:
        return previousCarrier.trim() !== '' && previousBill.trim() !== '';
      case 3:
        if (esimOptions.every(option => option === 'yes')) {
          return subscriptionType !== '';
        }
        return subscriptionType !== '' && shippingMethod !== '';
      case 4:
      case 5:
        return true;
      default:
        return false;
    }
  };

  // Handler functions for IMEI fields
  const handleIMEIChange = (index, value) => {
    const newImeis = [...imeis];
    newImeis[index] = value;
    setImeis(newImeis);

    const newValidation = [...imeiValidation];
    newValidation[index] = validateIMEI(value) || value === '';
    setImeiValidation(newValidation);
  };

  const handleEsimChange = (index, value) => {
    const newEsimOptions = [...esimOptions];
    newEsimOptions[index] = value;
    setEsimOptions(newEsimOptions);
    console.log(`eSIM option for line ${index + 1} changed to:`, value);
  };

  const handleImei2Change = (index, value) => {
    const newImei2Values = [...imei2Values];
    newImei2Values[index] = value;
    setImei2Values(newImei2Values);
  };

  // Handler functions for phone number fields
  const handlePhoneNumberChange = (index, value) => {
    const newPhoneNumbers = [...phoneNumbers];
    newPhoneNumbers[index] = value;
    setPhoneNumbers(newPhoneNumbers);

    const newValidation = [...phoneNumberValidation];
    newValidation[index] = validatePhoneNumber(value) || value === '';
    setPhoneNumberValidation(newValidation);
  };

  const handleNext = () => {
    if (!validateForm()) {
      setSnackbarMessage('Please fill in all required fields correctly.');
      setSnackbarOpen(true);
      return;
    }

    if (activeStep === steps.length - 3) {
      console.log('Circle Data from planData:', planData?.planData?.circle);

      // Prepare line details with all IMEIs and eSIM choices
      const lineDetails = imeis.map((imeiValue, index) => ({
        imei: imeiValue,
        esim: esimOptions[index] === 'yes',
        imei2: esimOptions[index] === 'yes' ? imei2Values[index] : '',
        lineNumber: index + 1,
        phoneNumber: portNumber === 'yes' ? phoneNumbers[index] : null
      }));

      // Determine if ALL lines are using eSIM
      const allLinesUseEsim = esimOptions.every(option => option === 'yes');

      // Track if ANY line is using eSIM
      const hasAnyEsim = esimOptions.some(option => option === 'yes');

      // Calculate the correct tier price based on total members
      const circle = planData?.planData?.circle;
      const existingMembers = (circle?.family_size || 0) + (circle?.total_joiners || 0);
      const totalMembers = existingMembers + lineCount;

      // Get pricing tiers from the plan data
      const pricingTiers = planData?.planData?.selected_plan?.pricing?.pricing_tiers || [];
      // Sort tiers by line count to ensure proper ordering
      const sortedTiers = [...pricingTiers].sort((a, b) => a.line_count - b.line_count);

      // Find the appropriate tier based on total members
      let perLineCost = planData?.planData?.selected_plan?.per_line_cost || 0;
      const tierMatch = sortedTiers.find(tier => tier.line_count >= totalMembers);

      if (tierMatch) {
        perLineCost = tierMatch.price_per_line;
        console.log(`Found pricing tier: ${tierMatch.line_count} lines at $${perLineCost}/line`);
      } else if (sortedTiers.length > 0) {
        // If no matching tier found, use the last tier (highest member count)
        perLineCost = sortedTiers[sortedTiers.length - 1].price_per_line;
        console.log(`Using highest tier price: $${perLineCost}/line`);
      }

      // Extract carrier data
      const carrierData = planData?.planData?.carrier || {};

      // Calculate totals based on line count
      const totalMobileCost = perLineCost * lineCount;
      const perLineTaxes = 0;
      const totalTaxes =  0;

      // Use carrier data for activation fee
      const perLineActivationFee = carrierData.activation_fee_amount || 35.00;
      const totalActivationFee = perLineActivationFee * lineCount;

      // Calculate subscription costs
      const perLineSubscriptionCost = subscriptionType === 'annual' ? 96.00 : 36.00;
      const totalSubscriptionCost = perLineSubscriptionCost * lineCount;

      // Calculate shipping cost - ONLY if not all lines use eSIM
      const isExpedited = shippingMethod === 'expedited';
      const shippingCost = allLinesUseEsim ? 0 : (isExpedited ? 15.00 : 8.00);

      // Calculate total costs
      const verizonTotal = totalMobileCost + totalActivationFee;
      const circledinTotal = totalSubscriptionCost + shippingCost;
      const totalDueToday = verizonTotal + circledinTotal;

      // Format the plan name to include line count if more than 1
      const planNameWithLines = lineCount > 1
        ? `${selectedPlan?.name || "Verizon - Unlimited Welcome"} (${lineCount} lines)`
        : selectedPlan?.name || "Verizon - Unlimited Welcome";

      // FIXED: Use actual features from the selected plan
      const planFeatures = selectedPlan?.features || [];
      console.log('Using actual plan features:', planFeatures);

      const reviewData = {
        planDetails: {
          circleName: planData?.planData?.circle?.name,
          planName: planNameWithLines,
          planId: selectedPlan?.id,
          lines: lineCount,
          // Total costs for display
          monthlyRate: totalMobileCost,
          taxes: 0,
          totalMonthly: totalMobileCost + totalTaxes,

          // Add per line and total activation fee info
          activationFeePerLine: perLineActivationFee,
          totalActivationFee: totalActivationFee,

          // Add total due today
          totalDueToday: totalDueToday,

          carrier: planData?.planData?.carrier?.name || 'verizon',
          // FIXED: Use actual features from the selected plan
          features: planFeatures.map(feature => ({
            title: feature.title || feature.name,
            description: feature.short_description || feature.description
          }))
        },
        userDetails: {
          numLines: lineCount,
          type: portNumber === 'yes' ? "porting" : "new",
          lineDetails: lineDetails,
          // Set both esim and hasEsim properties for compatibility
          esim: allLinesUseEsim,
          hasEsim: hasAnyEsim,
          ...(portNumber === 'no' && {
            area_code: areaCode || '',
            zip_code: zipCode || '',
          }),
          ...(portNumber === 'yes' && {
            phoneNumbers: phoneNumbers,
          }),
          previousCarrier: previousCarrier,
          previousBill: previousBill,
          port_number: portNumber === 'yes',
          circle_id: planData?.planData?.circle?.id,
          circle_name: planData?.planData?.circle?.name,
          joining_condition: planData?.joining_condition || "switching_carriers"
        },
        subscriptionDetails: {
          type: subscriptionType,
          shipping: allLinesUseEsim ? 'eSIM' : shippingMethod,

          // Add subscription and shipping cost information
          perLineSubscriptionCost: perLineSubscriptionCost,
          totalSubscriptionCost: totalSubscriptionCost,
          shippingCost: shippingCost // Will be 0 if all lines use eSIM
        },

        // Save stripe price ID info for payment processing
        stripeInfo: {
          activationFeePriceId: carrierData.activation_fee_price_id || ''
        }
      };

      console.log('Review data:', reviewData);

      setReviewData(reviewData);
    }

    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const renderEsimActivationSteps = () => {
    return (
      <EsimInfoBox>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
          <AlertCircle size={22} color={theme.palette.primary.main} style={{ marginTop: '2px' }} />
          <Box>
            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
              eSIM Activation Process
            </Typography>

            <List dense disablePadding>
              <StepItem disableGutters>
                <ListItemIcon>
                  <CheckCircle size={18} color={theme.palette.primary.main} />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography variant="body2">
                      <Box component="span" sx={{ fontWeight: 600 }}>Step 1:</Box> Your Account Owner will process your eSIM activation
                    </Typography>
                  }
                />
              </StepItem>

              <StepItem disableGutters>
                <ListItemIcon>
                  <MessageSquare size={18} color={theme.palette.primary.main} />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography variant="body2">
                      <Box component="span" sx={{ fontWeight: 600 }}>Step 2:</Box> You'll receive a <Box component="span" sx={{ fontWeight: 600 }}>text message</Box> or <Box component="span" sx={{ fontWeight: 600 }}>banner notification</Box>
                    </Typography>
                  }
                />
              </StepItem>

              <StepItem disableGutters>
                <ListItemIcon>
                  <Zap size={18} color={theme.palette.primary.main} />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography variant="body2">
                      <Box component="span" sx={{ fontWeight: 600 }}>Step 3:</Box> Follow the prompt instructions to complete setup
                    </Typography>
                  }
                />
              </StepItem>
            </List>
          </Box>
        </Box>
      </EsimInfoBox>
    );
  };

  const renderIMEIInstructions = () => {
    return (
      <InstructionAccordion>
        <AccordionSummary
          expandIcon={<ChevronDown size={20} />}
          sx={{ alignItems: 'center' }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <InfoIcon size={20} color={theme.palette.primary.main} />
            <Typography sx={{ fontWeight: 500 }}>How to find your IMEI</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
            Your IMEI is a 15-digit unique identifier that helps verify device compatibility.
          </Typography>

          <Divider sx={{ my: 1.5 }} />

          <ImeiMethodContainer>
            <Phone size={20} />
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Using your dial pad:</Typography>
              <Typography variant="body2" gutterBottom>
                Enter <Box component="span" sx={{ fontWeight: 600 }}>*#06#</Box> on your phone's dial pad
              </Typography>
            </Box>
          </ImeiMethodContainer>

          <ImeiMethodContainer>
            <Smartphone size={20} />
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Android devices:</Typography>
              <Typography variant="body2" gutterBottom>
                Go to Settings → About device → Status
              </Typography>
            </Box>
          </ImeiMethodContainer>

          <ImeiMethodContainer>
            <Monitor size={20} />
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>iPhone devices:</Typography>
              <Typography variant="body2" gutterBottom>
                Go to Settings → General → About
              </Typography>
            </Box>
          </ImeiMethodContainer>

          <ImeiMethodContainer>
            <Tablet size={20} />
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Windows Phone:</Typography>
              <Typography variant="body2" gutterBottom>
                Go to Settings → About → More info
              </Typography>
            </Box>
          </ImeiMethodContainer>

          <Box sx={{ mt: 2, p: 1.5, backgroundColor: theme.palette.info.light, borderRadius: 1 }}>
            <Typography variant="body2">
              <strong>Note:</strong> For dual-SIM phones, you may have two IMEI numbers (IMEI1 and IMEI2).
              The IMEI2 field will appear if you select "Yes" to eSIM activation.
            </Typography>
          </Box>
        </AccordionDetails>
      </InstructionAccordion>
    );
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            <Typography variant="h5" gutterBottom align="center" sx={{ mb: 3 }}>
              Will your phone{lineCount > 1 ? 's' : ''} work on this network?
            </Typography>
            <Typography variant="body1" gutterBottom align="center" sx={{ mb: 4, maxWidth: '650px', mx: 'auto' }}>
              Your device{lineCount > 1 ? 's' : ''} must be unlocked. We recommend checking with the specific carrier
              to see if your device{lineCount > 1 ? 's are' : ' is'} compatible on their network.
              Optionally, type in your IMEI{lineCount > 1 ? 's' : ''} and we'll be happy to check for you.
            </Typography>

            {imeis.map((imeiValue, index) => (
              <LineContainer
                key={`line-${index}`}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <SectionTitle color={theme.palette.primary.main}>
                  Line {index + 1} Details
                </SectionTitle>

                <StyledTextField
                  fullWidth
                  label={`IMEI for Line ${index + 1}`}
                  value={imeiValue}
                  onChange={(e) => handleIMEIChange(index, e.target.value)}
                  error={!imeiValidation[index] && imeiValue !== ''}
                  helperText={!imeiValidation[index] && imeiValue !== '' ? 'Invalid IMEI (15 digits required)' : ''}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Smartphone size={20} />
                      </InputAdornment>
                    ),
                  }}
                />

                <Typography variant="h6" gutterBottom fontWeight="bold" textAlign="center" sx={{ mt: 1, mb: 1 }}>
                  Interested in activating eSIM for Line {index + 1}?
                </Typography>

                <RadioOptionBox>
                  <RadioGroup
                    row
                    value={esimOptions[index]}
                    onChange={(e) => handleEsimChange(index, e.target.value)}
                  >
                    <FormControlLabel value="yes" control={<Radio color="primary" />} label="Yes" />
                    <FormControlLabel value="no" control={<Radio color="primary" />} label="No" />
                  </RadioGroup>
                </RadioOptionBox>

                <Collapse in={esimOptions[index] === 'yes'}>
                  {renderEsimActivationSteps()}
                  <StyledTextField
                    fullWidth
                    label={`IMEI2 + EID for Line ${index + 1}`}
                    value={imei2Values[index]}
                    onChange={(e) => handleImei2Change(index, e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Smartphone size={20} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Collapse>
              </LineContainer>
            ))}

            {renderIMEIInstructions()}
          </motion.div>
        );

      case 1:
        return (
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            <Typography variant="h5" gutterBottom fontWeight="bold" align="center" sx={{ mb: 3 }}>
              Would you like to transfer your {lineCount > 1 ? 'numbers' : 'number'} over to "{carrierName}"
            </Typography>

            <RadioOptionBox sx={{ maxWidth: '300px', mx: 'auto', mb: 4 }}>
              <RadioGroup
                row
                value={portNumber}
                onChange={(e) => setPortNumber(e.target.value)}
              >
                <FormControlLabel value="yes" control={<Radio color="primary" />} label="Yes" />
                <FormControlLabel value="no" control={<Radio color="primary" />} label="No" />
              </RadioGroup>
            </RadioOptionBox>

            <Collapse in={portNumber === 'yes'}>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.2 }}
              >
                {/* Phone numbers to port - one for each line with consistent styling */}
                {phoneNumbers.map((phoneNumber, index) => (
                  <LineContainer
                    key={`phone-${index}`}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 + index * 0.1 }}
                  >
                    <SectionTitle color={theme.palette.primary.main}>
                      Line {index + 1} Phone Number
                    </SectionTitle>
                    <StyledTextField
                      fullWidth
                      label="Phone Number to Port"
                      value={phoneNumber}
                      onChange={(e) => handlePhoneNumberChange(index, e.target.value)}
                      error={!phoneNumberValidation[index] && phoneNumber !== ''}
                      helperText={!phoneNumberValidation[index] && phoneNumber !== '' ? 'Invalid phone number (10 digits required)' : ''}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Phone size={20} />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </LineContainer>
                ))}
              </motion.div>
            </Collapse>

            <Collapse in={portNumber === 'no'}>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.2 }}
              >
                <LocationContainer>
                  <SectionTitle color={theme.palette.info.main}>
                    Location
                  </SectionTitle>
                  <Typography variant="body1" gutterBottom align="left" sx={{ mb: 3 }}>
                    Please enter your ZIP code to get a new number. This helps us assign you a
                    number that's appropriate for your area.
                  </Typography>
                  <StyledTextField
                    fullWidth
                    label="ZIP Code"
                    value={zipCode}
                    onChange={(e) => setZipCode(e.target.value)}
                    error={!validateZipCode(zipCode) && zipCode !== ''}
                    helperText={!validateZipCode(zipCode) && zipCode !== '' ? 'Invalid ZIP code (format: 12345 or 12345-6789)' : ''}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <MapPin size={20} />
                        </InputAdornment>
                      ),
                    }}
                  />

                  <Typography variant="h6" gutterBottom fontWeight="bold" textAlign="left" sx={{ mt: 4, mb: 2 }}>
                    Preferred Area Code
                  </Typography>
                  <Typography variant="body1" gutterBottom align="left" sx={{ mb: 3 }}>
                    Optional: If your ZIP code has multiple area codes, please enter your preferred area code.
                    Subject to availability.
                  </Typography>
                  <StyledTextField
                    fullWidth
                    label="Area Code"
                    value={areaCode}
                    onChange={(e) => setAreaCode(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Hash size={20} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </LocationContainer>
              </motion.div>
            </Collapse>
          </motion.div>
        );

      case 2:
        return (
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            <PreviousCarrierContainer>
              <SectionTitle color={theme.palette.purple.main}>
                Previous Carrier
              </SectionTitle>
              <Typography variant="body1" gutterBottom align="left" sx={{ mb: 3 }}>
                Please let us know which carrier you're coming from. This information helps us
                provide a smoother transition to your new service.
              </Typography>
              <StyledTextField
                fullWidth
                label="Previous Carrier"
                value={previousCarrier}
                onChange={(e) => setPreviousCarrier(e.target.value)}
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Smartphone size={20} />
                    </InputAdornment>
                  ),
                }}
              />

              <Typography variant="h6" gutterBottom fontWeight="bold" textAlign="left" sx={{ mt: 4, mb: 2 }}>
                Previous Carrier Costs
              </Typography>
              <Typography variant="body1" gutterBottom align="left" sx={{ mb: 3 }}>
                Knowing your current monthly payment helps us understand the savings you'll
                enjoy with your new plan.
              </Typography>
              <StyledTextField
                fullWidth
                label="Monthly Bill Amount (per line)"
                value={previousBill}
                onChange={(e) => setPreviousBill(e.target.value)}
                required
                placeholder="75.00"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      $
                    </InputAdornment>
                  ),
                }}
              />

              <Box sx={{ mt: 3, p: 2, backgroundColor: theme.palette.purple.light, borderRadius: 1 }}>
                <Typography variant="body2">
                  <strong>Why we ask:</strong> This information helps us calculate your potential savings
                  with your new plan and identify any additional benefits or promotions you might be eligible for.
                </Typography>
              </Box>
            </PreviousCarrierContainer>
          </motion.div>
        );

      case 3:
        return (
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            <SubscriptionAndShipping
              subscriptionType={subscriptionType}
              setSubscriptionType={setSubscriptionType}
              shippingMethod={shippingMethod}
              setShippingMethod={setShippingMethod}
              isEsim={esimOptions.every(option => option === 'yes')}
            />
          </motion.div>
        );

      case 4:
        return (
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            <ReviewAndConfirm
              planDetails={reviewData?.planDetails}
              userDetails={reviewData?.userDetails}
              subscriptionDetails={reviewData?.subscriptionDetails}
              onEdit={handleBack}
              onProceedToPayment={handleNext}  // Make sure this prop is passed
            />
          </motion.div>
        );

      case 5:
        return (
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            <Payment
              reviewData={reviewData}
              onBack={handleBack}
              onPaymentSuccess={(paymentResult) => {
                navigate('/success', {
                  state: {
                    ...reviewData,
                    paymentResult
                  }
                });
              }}
            />
          </motion.div>
        );

      default:
        return null;
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <div className="switching-carrier-wrapper">
        <StyledContainer>
          <ProgressIndicator mb={4}>
            <Typography variant="body2" align="center" color="text.secondary" sx={{ mb: 1 }}>
              Estimated time to complete: 5 minutes
            </Typography>
            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{
                height: 8,
                borderRadius: 4,
                backgroundColor: '#E5E7EB',
                '& .MuiLinearProgress-bar': {
                  borderRadius: 4,
                  background: 'linear-gradient(90deg, #ff385c 0%, #ff5c83 100%)',
                }
              }}
            />
          </ProgressIndicator>

          <StyledPaper sx={{ mt: 5 }}>
            <form onSubmit={(e) => e.preventDefault()}>
              <CustomStepper activeStep={activeStep} steps={steps} />

              <Box sx={{ px: { xs: 1, sm: 3 }, py: 3 }}>
                {renderStepContent(activeStep)}
              </Box>

              {/* Only show bottom navigation buttons if not on the Review & Confirm step */}
              {activeStep !== 4 && (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    mt: 4,
                    flexDirection: isMobile ? 'column-reverse' : 'row',
                    gap: 2
                  }}
                >
                  {/* Existing button code remains unchanged */}
                  <ActionButton
                    variant="outlined"
                    onClick={handleBack}
                    disabled={activeStep === 0}
                    startIcon={<ArrowLeft size={18} />}
                    isback="true"
                    fullWidth={isMobile}
                  >
                    Back
                  </ActionButton>

                  {activeStep !== steps.length - 1 && (
                    <ActionButton
                      variant="contained"
                      onClick={handleNext}
                      endIcon={<ArrowRight size={18} />}
                      fullWidth={isMobile}
                    >
                      {activeStep === steps.length - 2 ? 'Proceed to Payment' : 'Next'}
                    </ActionButton>
                  )}
                </Box>
              )}
            </form>
          </StyledPaper>

          <Snackbar
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            open={snackbarOpen}
            autoHideDuration={3000}
            onClose={() => setSnackbarOpen(false)}
            message={snackbarMessage}
          />
        </StyledContainer>
      </div>
    </ThemeProvider>
  );
};

export default SwitchingCarriers;